import { useCallback, useEffect, useRef, useState } from 'react'
import type { HintSurface } from '../../services/dashboard-invalidation'
import { hintedRefresh } from '../../services/hinted-refresh'

// Ownership and SMS-route changes arrive as hints; the fallback reconciles a
// missed hint. Without live hints the caller's interval applies, as before.
const HINTED_FALLBACK_MS = 120_000
// Ownership reads the aggregate and its ownership record.
const DEFAULT_SURFACES: readonly HintSurface[] = ['ownership', 'aggregates']

export type AggregatePollStatus = 'idle' | 'loading' | 'ready' | 'failed'

type PollState<T> = {
  owner: string | null
  status: AggregatePollStatus
  value: T | null
}

/**
 * Keep one interaction aggregate's resource current for the selected
 * conversation: on ownership and conversation hints, with a fallback poll.
 *
 * Every request and result belongs to its owner: the tenant and aggregate it was
 * issued for. A result or failure is applied only while that owner is still
 * selected, a result must name the requested aggregate, and state kept for
 * another owner is never returned (the first render after a selection change
 * reads as loading). Leaving an owner (selection change, deselection, unmount)
 * cancels its request; that cancellation is not a failure, and every real
 * failure is reported as `failed`.
 */
export function useAggregatePoll<T extends { aggregateId: string }>(
  read:
    | ((
        tenantId: string,
        aggregateId: string,
        signal: AbortSignal
      ) => Promise<T>)
    | undefined,
  tenantId: string,
  aggregateId: string | null,
  intervalMs = 5_000,
  surfaces: readonly HintSurface[] = DEFAULT_SURFACES
) {
  // A stable identity for the subscription, whatever array the caller passes.
  const surfaceKey = surfaces.join(',')
  const owner = read && aggregateId ? `${tenantId}:${aggregateId}` : null
  const selected = useRef<string | null>(null)
  const inFlight = useRef<{
    owner: string
    controller: AbortController
  } | null>(null)
  // A failed or unavailable read recovers without a hint of its own (an SMS
  // route clears when its projection finishes), so it keeps the fast cadence.
  const lastReadFailed = useRef(false)
  const scheduled = useRef<(() => Promise<void>) | null>(null)
  const [state, setState] = useState<PollState<T>>({
    owner: null,
    status: 'idle',
    value: null,
  })

  const refresh = useCallback(async () => {
    if (!read || !aggregateId) return
    const requestOwner = `${tenantId}:${aggregateId}`
    if (
      selected.current !== requestOwner ||
      inFlight.current?.owner === requestOwner
    ) {
      return
    }
    const controller = new AbortController()
    inFlight.current = { owner: requestOwner, controller }
    try {
      const value = await read(tenantId, aggregateId, controller.signal)
      if (selected.current !== requestOwner) return
      lastReadFailed.current = value.aggregateId !== aggregateId
      setState(
        value.aggregateId === aggregateId
          ? { owner: requestOwner, status: 'ready', value }
          : (previous) => failed(previous, requestOwner)
      )
    } catch {
      // Leaving the owner cancelled this request; it is not a failure of the
      // conversation selected now.
      if (controller.signal.aborted || selected.current !== requestOwner) return
      lastReadFailed.current = true
      setState((previous) => failed(previous, requestOwner))
    } finally {
      if (inFlight.current?.controller === controller) inFlight.current = null
    }
  }, [aggregateId, read, tenantId])

  useEffect(() => {
    selected.current = owner
    lastReadFailed.current = false
    if (!owner) return
    const wanted = surfaceKey.split(',') as HintSurface[]
    const stop = hintedRefresh(tenantId, wanted, refresh, {
      healthyMs: HINTED_FALLBACK_MS,
      unhealthyMs: intervalMs,
      urgent: () => lastReadFailed.current,
    })
    scheduled.current = stop.refresh
    return () => {
      stop()
      if (scheduled.current === stop.refresh) scheduled.current = null
      selected.current = null
      const request = inFlight.current
      if (request?.owner === owner) {
        inFlight.current = null
        request.controller.abort()
      }
      setState((previous) =>
        previous.owner === owner
          ? { owner: null, status: 'idle', value: null }
          : previous
      )
    }
  }, [intervalMs, owner, refresh, surfaceKey, tenantId])

  // A command result applies only to the conversation it was issued for.
  const accept = useCallback(
    (value: T) => {
      if (!owner || selected.current !== owner) return
      if (value.aggregateId !== aggregateId) return
      setState({ owner, status: 'ready', value })
    },
    [aggregateId, owner]
  )

  const current: PollState<T> =
    state.owner === owner
      ? state
      : { owner, status: owner ? 'loading' : 'idle', value: null }
  // Callers' refreshes (after a failed command) share the schedule, so a
  // failure there also restores the fast cadence.
  const refreshNow = useCallback(
    () => (scheduled.current ? scheduled.current() : refresh()),
    [refresh]
  )
  return {
    status: current.status,
    value: current.value,
    refresh: refreshNow,
    accept,
  }
}

function failed<T>(previous: PollState<T>, owner: string): PollState<T> {
  return {
    owner,
    status: 'failed',
    value: previous.owner === owner ? previous.value : null,
  }
}
